import { useFilePreview } from 'api/requests';
import { OnboardingStatusWrapper } from 'components';
import { isAccountDisabled } from 'config';
import { openAsyncLink } from 'helpers';
import { isLegalClientRepresentative, isPersonClient } from 'helpers/client';
import { useTranslation } from 'libs/i18n';
import { ROUTES } from 'libs/navigation';
import { useUser } from 'libs/providers';
import { Box, Button, LinkLabel, Stack } from 'libs/ui';

import { DocumentsStyled } from './OnboardingSuccessStatus.styles';
import { SubTitle } from './SubTitle';

export interface OnboardingSuccessStatusProps {
  documents: { id?: string; label: string; href?: string }[];
}

export const OnboardingSuccessStatus = ({
  documents,
}: OnboardingSuccessStatusProps) => {
  const { t } = useTranslation();
  const { getFileLink } = useFilePreview();

  const { client } = useUser();

  const showContinueButton =
    !isAccountDisabled() &&
    client?.type &&
    (isPersonClient(client.type) || isLegalClientRepresentative(client.type));

  const onClickDocument = async (id: string) => {
    openAsyncLink(() => getFileLink(id));
  };

  const renderDocumentBox = (
    label: string,
    href?: string,
    onClickDocument?: () => void
  ) => (
    <Box className="onboardingSuccessStatus-documentBox">
      <LinkLabel
        as={href ? undefined : 'button'}
        href={href}
        label={label}
        mode="text"
        target="_blank"
        typographyVariant="bodyBold"
        typographyVariantMobile="captionBold"
        onClick={onClickDocument}
      />
    </Box>
  );

  return (
    <OnboardingStatusWrapper
      subtitle={<SubTitle />}
      title={t('onboarding-success-status.title')}
    >
      {documents.length && (
        <DocumentsStyled>
          {documents.map((d) =>
            renderDocumentBox(
              d.label,
              d.href,
              () => d.id && onClickDocument(d.id)
            )
          )}
        </DocumentsStyled>
      )}
      {showContinueButton && (
        <Stack alignItems="flex-end">
          <Button href={ROUTES.account.fullPath} label={t('common.continue')} />
        </Stack>
      )}
    </OnboardingStatusWrapper>
  );
};
